import {
  availabilitiesAtom,
  basicInfoAtom,
  categoriesAtom,
  deliveryMenuAtom,
  deliveryServiceAtom,
  itemNamesAtom,
  itemOffersAtom,
  itemsAtom,
  merchantStatusAtom,
  optionGroupsAtom,
  takeoutServiceAtom,
} from "@/atoms";
import { activeBenefitsAtom } from "@/atoms/active-benefits";
import { tableAtom } from "@/atoms/table";
import { deriveServiceMenuData } from "@/lib/derive-service-menu-data";
import { getStoredCompany } from "@/lib/company-storage";
import { fetchActiveBenefits } from "@/lib/loyalty-benefits";
import { ApiData } from "@/types";
import { useMerchantMetadata } from "@/hooks/use-merchant-metadata";
import { useAtomValue, useSetAtom, type WritableAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import { useEffect, useMemo, useRef } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HydrationTuples = (readonly [WritableAtom<unknown, any[], any>, unknown])[];

/**
 * Hydrates the store-wide atoms and the storefront catalog: the DELIVERY menu, or the TAKEOUT menu in a
 * store that only takes away. At a table (`?table=`) the INDOOR catalog from IndoorApiHydration is browsed.
 */
export default function ApiHydration({ data, children }: { data: ApiData; children: React.ReactNode }) {
  const derived = useMemo(
    () => deriveServiceMenuData(data, "DELIVERY") ?? deriveServiceMenuData(data, "TAKEOUT"),
    [data],
  );
  const deliveryService = derived?.service.serviceType === "DELIVERY" ? derived.service : null;
  const takeoutService = useMemo(
    () => data.services.find((s) => s.serviceType === "TAKEOUT") ?? null,
    [data],
  );
  const isInitialMount = useRef(true);
  const table = useAtomValue(tableAtom);
  const isTableMode =
    table !== null || new URLSearchParams(window.location.search).has("table");

  const setDeliveryService = useSetAtom(deliveryServiceAtom);
  const setDeliveryMenu = useSetAtom(deliveryMenuAtom);
  const setTakeoutService = useSetAtom(takeoutServiceAtom);
  const setMerchantStatus = useSetAtom(merchantStatusAtom);
  const setBasicInfo = useSetAtom(basicInfoAtom);
  const setCategories = useSetAtom(categoriesAtom);
  const setItemOffers = useSetAtom(itemOffersAtom);
  const setItems = useSetAtom(itemsAtom);
  const setOptionGroups = useSetAtom(optionGroupsAtom);
  const setAvailabilities = useSetAtom(availabilitiesAtom);
  const setItemNames = useSetAtom(itemNamesAtom);
  const setActiveBenefits = useSetAtom(activeBenefitsAtom);

  useMerchantMetadata();

  useEffect(() => {
    const company = getStoredCompany();
    if (!company) return;
    fetchActiveBenefits(company.id)
      .then(setActiveBenefits)
      .catch(() => setActiveBenefits(null));
  }, [setActiveBenefits]);

  const hydrationTuples: HydrationTuples = [
    [merchantStatusAtom, data.status],
    [takeoutServiceAtom, takeoutService],
    [basicInfoAtom, data.basicInfo],
    [itemsAtom, data.items],
    [optionGroupsAtom, data.optionGroups],
    [availabilitiesAtom, data.availabilities],
    ...(derived
      ? ([
          [deliveryServiceAtom, deliveryService],
          [deliveryMenuAtom, derived.menu],
          [categoriesAtom, derived.categories],
          [itemOffersAtom, derived.itemOffers],
          [itemNamesAtom, derived.itemNames],
        ] as HydrationTuples)
      : []),
  ];
  useHydrateAtoms(hydrationTuples);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setMerchantStatus(data.status);
    setTakeoutService(takeoutService);
    setBasicInfo(data.basicInfo);
    setItems(data.items);
    setOptionGroups(data.optionGroups);
    setAvailabilities(data.availabilities);
    if (!derived) return;

    setDeliveryService(deliveryService);
    setDeliveryMenu(derived.menu);
    setCategories(derived.categories);
    setItemOffers(derived.itemOffers);
    setItemNames(derived.itemNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!derived && !isTableMode) return null;

  return children;
}
